/**
 * Translation utilities for handling multi-language content
 * 
 * Backend structure: { translations: { "en": { "name": "...", "description": "..." }, "tr": { ... } } }
 */

/**
 * Get translated field value with fallback logic
 * @param {Object} translations - Translations object from backend
 * @param {string} lang - Requested language code (e.g., 'en', 'tr', 'de')
 * @param {string} field - Field name to retrieve (e.g., 'name', 'description')
 * @param {string} fallbackLang - Fallback language if requested lang not found (default: 'en')
 * @returns {string} Translated value or empty string if not found
 */
export function getTranslation(translations, lang, field, fallbackLang = 'en') {
  if (!translations || typeof translations !== 'object') {
    return '';
  }

  // Try requested language
  if (translations[lang] && translations[lang][field]) {
    return translations[lang][field];
  }

  // Try fallback language
  if (fallbackLang && translations[fallbackLang] && translations[fallbackLang][field]) {
    return translations[fallbackLang][field];
  }

  // Return first available translation for this field
  for (const langCode in translations) {
    if (translations[langCode] && translations[langCode][field]) {
      return translations[langCode][field];
    }
  }

  return '';
}

/**
 * Get all available languages for a translation object
 * @param {Object} translations - Translations object
 * @returns {string[]} Array of language codes
 */
export function getAvailableLanguages(translations) {
  if (!translations || typeof translations !== 'object') {
    return [];
  }
  return Object.keys(translations);
}

/**
 * Set translation for a specific language and field
 * @param {Object} translations - Existing translations object
 * @param {string} lang - Language code
 * @param {string} field - Field name
 * @param {string} value - Value to set
 * @returns {Object} Updated translations object
 */
export function setTranslation(translations = {}, lang, field, value) {
  const updated = { ...translations };
  if (!updated[lang]) {
    updated[lang] = {};
  }
  updated[lang] = { ...updated[lang], [field]: value };
  return updated;
}

/**
 * Check if translations object has content for a specific field
 * @param {Object} translations - Translations object
 * @param {string} field - Field name to check
 * @returns {boolean} True if any language has this field
 */
export function hasTranslation(translations, field) {
  if (!translations || typeof translations !== 'object') {
    return false;
  }
  
  for (const lang in translations) {
    if (translations[lang] && translations[lang][field]) {
      return true;
    }
  }
  return false;
}

/**
 * Create translations object from old format (name_en, name_tr, etc.)
 * @param {Object} oldFormat - Object with name_en, name_tr, description_en, description_tr fields
 * @returns {Object} Translations object in new format
 */
export function migrateOldFormat(oldFormat) {
  const translations = {};
  
  // Migrate name fields
  if (oldFormat.name_en) {
    if (!translations.en) translations.en = {};
    translations.en.name = oldFormat.name_en;
  }
  if (oldFormat.name_tr) {
    if (!translations.tr) translations.tr = {};
    translations.tr.name = oldFormat.name_tr;
  }
  
  // Migrate description fields
  if (oldFormat.description_en) {
    if (!translations.en) translations.en = {};
    translations.en.description = oldFormat.description_en;
  }
  if (oldFormat.description_tr) {
    if (!translations.tr) translations.tr = {};
    translations.tr.description = oldFormat.description_tr;
  }
  
  return translations;
}

/**
 * Validate that translations object has at least one language with required field
 * @param {Object} translations - Translations object
 * @param {string} requiredField - Field that must exist (default: 'name')
 * @returns {boolean} True if valid
 */
export function validateTranslations(translations, requiredField = 'name') {
  if (!translations || typeof translations !== 'object') {
    return false;
  }
  
  for (const lang in translations) {
    if (translations[lang] && translations[lang][requiredField]) {
      return true;
    }
  }
  
  return false;
}

/**
 * Search within translations for a query string
 * @param {Object} translations - Translations object
 * @param {string} query - Search query
 * @param {string[]} fields - Fields to search in (default: ['name', 'description'])
 * @returns {boolean} True if query found in any translation
 */
export function searchTranslations(translations, query, fields = ['name', 'description']) {
  if (!translations || !query) {
    return false;
  }
  
  const lowerQuery = query.toLowerCase();
  
  for (const lang in translations) {
    for (const field of fields) {
      const value = translations[lang]?.[field];
      if (value && value.toLowerCase().includes(lowerQuery)) {
        return true;
      }
    }
  }
  
  return false;
}
